import PropTypes, { shape } from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { errorClear, errorShow } from '../../Utility/Form'
import { loqateValidateEmail } from '../../Utility/Utility'

/**
 * Primary UI component for user interaction
 */
export const FormInput = ({ label, placeholder, name, required, step, type, validationHash, validationMessages, value, ...props }) => {
  const [inputValue, inputValueSet] = useState(value)
  const [inputIsInvalid, inputIsInvalidSet] = useState(false)
  const [hasInitialised, hasInitialisedSet] = useState(false)
  const input = useRef(null)

  /**
   * The error ID.
   *
   * @type {string}
   */
  const errorId = `${name}_error`

  /**
   * The error message class
   *
   * @type {string}
   */
  const errorSpan = 'b-form-input__error-message'

  /**
  * Format sort code value to a maximum of 8 characters.
  *
  * @param {Event} event
  *   The triggered keydown event
  */
  const formatSortCode = (event) => {
    const sortCodeInput = input.current;
    sortCodeInput.setAttribute('maxLength', 8);
    if (event.code != 'Backspace' &&
      (sortCodeInput.value.length === 2 || sortCodeInput.value.length === 5)) {
      if (event.code != 'Minus') {
        sortCodeInput.value += '-';
      }
    }
  }

  /**
   * Handle any form changes.
   *
   * @param {Event} event
   *   The triggered change event
   */
  const handlerInputChange = event => {
    if (event.target.id === 'account_sort_code' && event.type === 'keydown') {
     formatSortCode(event);
    }
    inputValueSet(event.target.value)
  }

  /**
   * Handle any form changes.
   *
   * @param {Event} event
   *   The triggered change event
   */
  const handlerInputBlur = event => {
    if (event.target.value) {
      validate()

      if (event.target.id === 'email_address' || event.target.id === 'recipient_e_card_email') {
        if (input.current.validity.valid) {
        loqateValidateEmail(event.target.value, props.loqateKey, (hasErrors) => {
          if (hasErrors) {
            errorShow(input.current, `.${errorSpan}`, validationAttributes['data-error-patternmismatch-message'])
            inputIsInvalidSet(true)
          } else {
            errorClear(input.current, `.${errorSpan}`)
            inputIsInvalidSet(false)
          }
        })
      }
      }
    }
  }

  /**
   * Validate the element.
   */
  const validate = () => {
    if (input.current.validity.valid) {
      errorClear(input.current, `.${errorSpan}`)
      inputIsInvalidSet(false)
    }
    else {
      errorShow(input.current, `.${errorSpan}`)
      inputIsInvalidSet(true)
    }
  }

  /**
   * Generate the validation attributes.
   *
   * @type {{}}
   */
  const validationAttributes = validationMessages ? validationMessages.reduce((validationAttributes, current) => {
    if (current.message) {
      validationAttributes[`data-error-${current.type.toLowerCase()}-message`] = current.message
    }

    if (current.type === 'patternMismatch') {
      validationAttributes['pattern'] = current.pattern
    }

    if (current.type === 'rangeUnderflow') {
      validationAttributes['min'] = current.min
    }

    if (current.type === 'rangeOverflow') {
      validationAttributes['max'] = current.max
    }

    if (current.type === 'maxlength') {
      validationAttributes['maxLength'] = current.maxlength
    }

    if (current.type === 'minlength') {
      validationAttributes['minLength'] = current.minlength
    }

    return validationAttributes
  }, {}) : {}

  useEffect(() => {
    if (hasInitialised) {
      const handler = inputIsInvalid ? null : setTimeout(() => props.onInputChange(name, inputValue), 300)
      return () => clearTimeout(handler)
    }
  }, [inputValue, inputIsInvalid])

  useEffect(() => {
    if (validationHash && hasInitialised) {
      validate()
    }
  }, [inputValue, validationHash])

  useEffect(() => {
    hasInitialisedSet(true)
  }, [])

  useEffect(() => {
    if (value !== inputValue) {
      inputValueSet(value)
    }
  }, [value])

  return (
    <div className={'b-form-row'}>
      <div className={name == 'account_number' ? 'b-form-input account-number' : 'b-form-input'}
        data-type={type} data-state={inputIsInvalid ? 'error' : 'ok'}>
        <label htmlFor={name}>{label} {required ? '*' : null}</label>
        <input
          ref={input}
          placeholder={placeholder}
          type={type}
          id={name}
          name={name}
          aria-describedby={errorId}
          aria-required={required}
          aria-invalid={inputIsInvalid}
          step={step}
          value={inputValue === null ? '' : inputValue}
          required={required}
          onChange={handlerInputChange}
          onKeyUp={handlerInputChange}
          onKeyDown={handlerInputChange}
          onBlur={handlerInputBlur}
          onWheel={(e) => e.target.blur()}
          {...validationAttributes}
        />

        {required || Object.keys(validationAttributes).length > 0 ?
          <div className="b-form-input__error-wrapper">
            <span className={errorSpan} id={errorId} aria-live="assertive" />
          </div> : null}
      </div>
    </div>
  )
}

FormInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  step: PropTypes.string,
  validationHash: PropTypes.string,
  validationMessages: PropTypes.arrayOf(
    shape({
      type: PropTypes.string,
      message: PropTypes.string,
      pattern: PropTypes.string,
    })
  ),
  value: PropTypes.string,
}
